import { useQuery, useQueries } from '@tanstack/react-query';
import { useApi } from '@/providers/apiProvider';
import {
  ApiCartStatus,
  ApiLocation,
  ApiLocationHealth,
  ApiMe,
  ApiRegion,
  cartFromApi,
  meFromApi,
  storeFromApi,
} from '@/lib/adapter';
import type { Cart, Me, Store } from '@/lib/types';

/** Refresh model: fetch on mount + pull-to-refresh only. No polling. */
const NO_POLLING = {
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: 1,
} as const;

export function useMe() {
  const api = useApi();
  return useQuery<Me>({
    queryKey: ['me'],
    queryFn: async () => meFromApi((await api.get<ApiMe>('/users/me/')).data),
    ...NO_POLLING,
  });
}

/**
 * Customer ids the user can see, from the same endpoint the portal uses, so
 * access matches the portal: platform admins get every customer, everyone
 * else their linked customers.
 */
export function useCustomerIds() {
  const api = useApi();
  return useQuery<string[]>({
    queryKey: ['customers'],
    queryFn: async () =>
      (await api.get<{ id: string }[]>('/customers/')).data.map((c) => c.id),
    ...NO_POLLING,
  });
}

/**
 * Stores across all of the user's customers, already access-scoped
 * server-side. Region names resolve via the regions endpoint; failures fall
 * back to 'Unassigned' rather than blocking the store list.
 */
export function useStores(customerIds: string[]) {
  const api = useApi();
  const results = useQueries({
    queries: customerIds.map((customerId) => ({
      queryKey: ['stores', customerId],
      queryFn: async (): Promise<Store[]> => {
        const locations = (
          await api.get<ApiLocation[]>(`/customers/${customerId}/locations/`)
        ).data;
        const regionNames = new Map<string, string>();
        try {
          const regions = (
            await api.get<ApiRegion[]>(`/customers/${customerId}/regions/`)
          ).data;
          for (const r of regions) regionNames.set(r.id, r.name);
        } catch {
          /* scoped users may not list regions; group as Unassigned */
        }
        return locations.map((loc) => storeFromApi(loc, regionNames));
      },
      ...NO_POLLING,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const stores = results.flatMap((r) => r.data ?? []);
  stores.sort(
    (a, b) =>
      (a.region === 'Unassigned' ? 1 : 0) - (b.region === 'Unassigned' ? 1 : 0) ||
      a.region.localeCompare(b.region) ||
      a.name.localeCompare(b.name)
  );
  return { stores, isLoading };
}

export function useCartsStatus(locationId: string | null) {
  const api = useApi();
  return useQuery<Cart[]>({
    queryKey: ['carts-status', locationId],
    queryFn: async () => {
      const [status, health] = await Promise.all([
        api.get<ApiCartStatus[]>(`/locations/${locationId}/carts-status/`),
        // Fence and error marks; a failed health call must not blank the store.
        api
          .get<ApiLocationHealth>(`/locations/${locationId}/cart-health/`)
          .then((r) => r.data.carts)
          .catch(() => ({}) as ApiLocationHealth['carts']),
      ]);
      const now = Date.now();
      return status.data.map((c) =>
        cartFromApi(c, now, health[c.external_id]?.alerting)
      );
    },
    enabled: locationId !== null,
    ...NO_POLLING,
  });
}
